import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronRight,
  Package,
  RefreshCw,
  Users,
} from 'lucide-react';
import type {
  CanceladosVista,
  MejorClienteVista,
  PaqueteTopVista,
  ProductoTopVista,
  ResumenDiaVista,
} from '@shared/types';
import { diaLocalDesdeTexto, toLocalYYYYMMDD } from '@shared/dateLocal';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourReportes } from '@renderer/tours';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { ReporteContadoraPdf } from '@renderer/components/reportes/ReporteContadoraPdf';

/**
 * Sprint 152 — **Reportes, reconstruida.**
 *
 * ## De dónde sale
 *
 * El dueño mandó la pantalla marcada entera con flechas rojas: arriba **$11,544** del mes, abajo
 * **todo en ceros**, el mismo día. Cinco auditorías en paralelo la bloquearon, y el diagnóstico
 * fue más grande que el síntoma: **19 tarjetas, las mismas cuatro cifras repetidas dos veces, y
 * cuatro criterios distintos por debajo para contestar "cuánto se vendió"**.
 *
 * El Sprint 150 arregló el dinero. Éste arregla la pantalla.
 *
 * ## Las tres reglas que la gobiernan
 *
 * 1. **Un solo periodo manda en todo.** Antes había dos componentes consultando por su cuenta con
 *    ventanas de tiempo distintas; mientras existieran los dos, la contradicción volvía sola. Hoy
 *    el periodo se calcula una vez, aquí, y todas las consultas reciben el mismo.
 * 2. **Ninguna cifra se pinta dos veces.** Dos números con la misma etiqueta y valores distintos
 *    no son información de más: son un sistema que no se puede creer, ni siquiera en lo que
 *    acierta.
 * 3. **Un cero se explica con palabras.** `$0.00` en grande significaba a la vez "no hubo ventas",
 *    "no se pudo consultar" y "no tienes permiso". Ahora cada uno dice lo suyo.
 *
 * ## El orden
 *
 * Cinco focos, en el orden en que alguien lee a las once de la noche: cuánto entró · cómo pagaron
 * · qué hay que revisar · qué se vendió · lo de la contadora. Los dos últimos bloques van
 * plegados: son para otro momento del mes.
 */

/** Los periodos que se ofrecen. Cuatro: esto se consulta de pie y con gente esperando. */
type Periodo = 'dia' | 'semana' | 'mes' | 'rango';

function hace(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return toLocalYYYYMMDD(d);
}

export function ReportesPage() {
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const [periodo, setPeriodo] = useState<Periodo>('dia');
  const [dia, setDia] = useState(toLocalYYYYMMDD(new Date()));
  const [desdeTexto, setDesdeTexto] = useState(hace(29));
  const [hastaTexto, setHastaTexto] = useState(toLocalYYYYMMDD(new Date()));

  const [resumen, setResumen] = useState<ResumenDiaVista | null>(null);
  const [productos, setProductos] = useState<ProductoTopVista[]>([]);
  const [paquetes, setPaquetes] = useState<PaqueteTopVista[]>([]);
  const [clientes, setClientes] = useState<MejorClienteVista[]>([]);
  const [cancelados, setCancelados] = useState<CanceladosVista | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verContadora, setVerContadora] = useState(false);
  const esDueno = useSessionStore((s) => s.sesion?.rol) === 'dueño';
  const [verClientes, setVerClientes] = useState(false);

  /**
   * El periodo, calculado UNA vez y en hora local.
   *
   * `diaLocalDesdeTexto` ancla al mediodía: `new Date("2026-09-20")` es medianoche UTC, o sea la
   * víspera en México, y eso es lo que tuvo esta pantalla mostrando el día anterior durante cinco
   * meses (Sprint 150).
   */
  const rango = useMemo(() => {
    const finDe = (texto: string) => {
      const d = diaLocalDesdeTexto(texto);
      if (!d) return null;
      d.setHours(23, 59, 59, 999);
      return d;
    };
    const inicioDe = (texto: string) => {
      const d = diaLocalDesdeTexto(texto);
      if (!d) return null;
      d.setHours(0, 0, 0, 0);
      return d;
    };

    if (periodo === 'dia') {
      const i = inicioDe(dia);
      const f = finDe(dia);
      return i && f ? { desde: i, hasta: f } : null;
    }
    if (periodo === 'semana') {
      const i = inicioDe(hace(6));
      const f = finDe(toLocalYYYYMMDD(new Date()));
      return i && f ? { desde: i, hasta: f } : null;
    }
    if (periodo === 'mes') {
      const hoy = new Date();
      const i = new Date(hoy.getFullYear(), hoy.getMonth(), 1, 0, 0, 0, 0);
      const f = finDe(toLocalYYYYMMDD(hoy));
      return f ? { desde: i, hasta: f } : null;
    }
    const i = inicioDe(desdeTexto);
    const f = finDe(hastaTexto);
    return i && f && i <= f ? { desde: i, hasta: f } : null;
  }, [periodo, dia, desdeTexto, hastaTexto]);

  const cargar = useCallback(async () => {
    if (!rango) {
      setError('Ese periodo no se entiende. Revisa las fechas.');
      setCargando(false);
      return;
    }
    setCargando(true);
    const desde = rango.desde.toISOString();
    const hasta = rango.hasta.toISOString();
    const dto = { desde, hasta };

    const [r, p, pq, mc, can] = await Promise.all([
      window.api.reportes.ventasDelDia(desde, hasta),
      window.api.reportes.productosTop(desde, hasta),
      window.api.reportes.paquetesTop(desde, hasta),
      window.api.reportes.mejorCliente(dto, 5),
      window.api.reportes.cancelados(dto),
    ]);

    /*
      Si la consulta del dinero falla, se BORRA lo anterior. Antes se hacía `if (r.ok) set(...)`
      sin `else`: al cambiar de fecha con un error de por medio, la pantalla seguía enseñando los
      números del periodo anterior bajo el periodo nuevo. Un número viejo se lee como un número.
    */
    if (r.ok) {
      setResumen(r.data);
      setError(null);
    } else {
      setResumen(null);
      setError(r.error);
    }
    setProductos(p.ok ? p.data : []);
    setPaquetes(pq.ok ? pq.data : []);
    setClientes(mc.ok ? mc.data : []);
    setCancelados(can.ok ? can.data : null);
    setCargando(false);
  }, [rango]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const enPalabras = (d: Date) =>
    d.toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' });

  const tituloPeriodo = useMemo(() => {
    if (!rango) return 'Periodo no válido';
    if (periodo === 'dia') {
      const hoy = toLocalYYYYMMDD(new Date());
      if (dia === hoy) return `Hoy · ${enPalabras(rango.desde)}`;
      if (dia === hace(1)) return `Ayer · ${enPalabras(rango.desde)}`;
      return enPalabras(rango.desde);
    }
    if (periodo === 'semana') return 'Últimos 7 días';
    if (periodo === 'mes')
      return `Este mes · del 1 al ${rango.hasta.getDate()} de ${rango.desde.toLocaleDateString(locale, { month: 'long' })}`;
    return `Del ${enPalabras(rango.desde)} al ${enPalabras(rango.hasta)}`;
  }, [rango, periodo, dia, locale]);

  const botonPeriodo = (valor: Periodo, etiqueta: string) => (
    <button
      key={valor}
      type="button"
      onClick={() => setPeriodo(valor)}
      aria-pressed={periodo === valor}
      className={`min-h-[44px] rounded-lg border-2 px-4 text-sm font-semibold transition ${
        periodo === valor
          ? 'border-primary bg-primary text-white'
          : 'border-slate-300 bg-white text-slate-700 hover:border-primary'
      }`}
    >
      {etiqueta}
    </button>
  );

  const money = (n: number) => formatPrecio(n, monedaSimbolo, locale);

  // ───────────────────────────── Bloque 3: lo que hay que revisar ─────────────
  const porDevolver = resumen?.montoPorDevolverPendiente ?? 0;
  const hayQueRevisar =
    (cancelados?.cantidad ?? 0) > 0 ||
    (resumen?.cantidadItemsCancelados ?? 0) > 0 ||
    (resumen?.descuentoPromoTotal ?? 0) > 0 ||
    porDevolver > 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start gap-3">
        <BarChart3 className="mt-1 h-7 w-7 text-primary" aria-hidden="true" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Reportes</h1>
          {/* El periodo se dice en palabras y en grande: es el contexto de TODAS las cifras. */}
          <p className="text-sm font-semibold text-slate-700">{tituloPeriodo}</p>
        </div>
        <button onClick={() => void cargar()} className="btn-ghost min-h-[44px] text-sm">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Actualizar
        </button>
        {/*
          En variante discreta: en una pantalla de dinero, el objeto más llamativo no puede ser el
          botón de ayuda. Antes era ámbar, con borde grueso, parpadeando y con un distintivo rojo
          encima que además tapaba su propio texto.
        */}
        <HelpButton tour={tourReportes} label="Cómo leer los reportes" variant="subtle" />
      </header>

      <section className="card space-y-3" data-tour="reportes-periodo">
        <div className="flex flex-wrap gap-2">
          {botonPeriodo('dia', 'Un día')}
          {botonPeriodo('semana', 'Últimos 7 días')}
          {botonPeriodo('mes', 'Este mes')}
          {botonPeriodo('rango', 'Elegir fechas')}
        </div>

        {periodo === 'dia' && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              {/* El selector no tenía etiqueta: sólo un icono de calendario al lado. */}
              <label className="label" htmlFor="reporteDia">
                Día que quieres ver
              </label>
              <input
                id="reporteDia"
                type="date"
                className="input min-h-[44px]"
                value={dia}
                max={toLocalYYYYMMDD(new Date())}
                onChange={(e) => setDia(e.target.value)}
              />
            </div>
            <Calendar className="mb-3 h-5 w-5 text-slate-500" aria-hidden="true" />
          </div>
        )}

        {periodo === 'rango' && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="label" htmlFor="reporteDesde">
                Desde
              </label>
              <input
                id="reporteDesde"
                type="date"
                className="input min-h-[44px]"
                value={desdeTexto}
                max={hastaTexto}
                onChange={(e) => setDesdeTexto(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="reporteHasta">
                Hasta
              </label>
              <input
                id="reporteHasta"
                type="date"
                className="input min-h-[44px]"
                value={hastaTexto}
                min={desdeTexto}
                max={toLocalYYYYMMDD(new Date())}
                onChange={(e) => setHastaTexto(e.target.value)}
              />
            </div>
          </div>
        )}
      </section>

      {error ? (
        <div className="card border-2 border-amber-300 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
            <div>
              <div className="font-semibold text-amber-900">
                No se pudieron traer los números de este periodo.
              </div>
              <p className="mt-1 text-sm text-amber-900">{error}</p>
              <button onClick={() => void cargar()} className="btn-ghost mt-2 min-h-[44px] text-sm">
                Volver a intentar
              </button>
            </div>
          </div>
        </div>
      ) : cargando ? (
        <div className="card py-10 text-center text-slate-600">Trayendo los números…</div>
      ) : !resumen || resumen.cantidadPedidosPagados === 0 ? (
        /*
          Un periodo sin ventas NO se dibuja como una cuadrícula de ceros: se dice con palabras y
          se ofrece a dónde ir. Un `$0.00` en 30px es indistinguible de una consulta que falló.
        */
        <div className="card space-y-3 py-8 text-center">
          <div className="text-lg font-semibold text-slate-800">
            No se cobró nada en este periodo.
          </div>
          <p className="text-sm text-slate-600">{tituloPeriodo}</p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => {
                setPeriodo('dia');
                setDia(hace(1));
              }}
              className="btn-ghost min-h-[44px] text-sm"
            >
              Ver el día de ayer
            </button>
            <button onClick={() => setPeriodo('mes')} className="btn-ghost min-h-[44px] text-sm">
              Ver el mes completo
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ── 1. Cuánto entró ─────────────────────────────────────────── */}
          <div className="grid gap-3 lg:grid-cols-2">
            <section className="card border-2 border-emerald-200 bg-emerald-50">
              <div className="text-sm font-semibold text-emerald-900">Cuánto entró</div>
              <div className="mt-1 font-mono text-4xl font-bold text-emerald-900">
                {money(resumen.totalVentas)}
              </div>
              <div className="mt-2 text-sm text-emerald-900">
                {resumen.cantidadPedidosPagados}{' '}
                {resumen.cantidadPedidosPagados === 1 ? 'pedido cobrado' : 'pedidos cobrados'} ·{' '}
                {money(resumen.ticketPromedio)} cada uno en promedio
              </div>
            </section>

            {/* ── 2. Cómo pagaron ──────────────────────────────────────── */}
            <section className="card">
              <div className="text-sm font-semibold text-slate-700">Cómo pagaron</div>
              <dl className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt>Efectivo</dt>
                  <dd className="font-mono font-semibold">{money(resumen.totalEfectivo)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Tarjeta</dt>
                  <dd className="font-mono font-semibold">{money(resumen.totalTarjeta)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Transferencia</dt>
                  <dd className="font-mono font-semibold">{money(resumen.totalTransferencia)}</dd>
                </div>
                {/* Cuadra con la tarjeta de al lado: es la misma cuenta, vista por método. */}
                <div className="flex justify-between border-t border-slate-300 pt-1 font-bold">
                  <dt>Total</dt>
                  <dd className="font-mono">{money(resumen.totalVentas)}</dd>
                </div>
              </dl>
            </section>
          </div>

          {/* ── 3. Para revisar — sólo aparece si hay algo que revisar ──── */}
          {hayQueRevisar ? (
            <section className="card border-2 border-amber-200 bg-amber-50">
              <div className="text-sm font-semibold text-amber-900">Para revisar</div>
              <ul className="mt-2 space-y-1 text-sm text-amber-900">
                {porDevolver > 0 && (
                  <li>
                    <strong>{money(porDevolver)}</strong> cobrados que hay que devolverle a
                    clientes: son pedidos que se pagaron y no se entregaron.
                  </li>
                )}
                {(cancelados?.cantidad ?? 0) > 0 && (
                  <li>
                    {cancelados!.cantidad}{' '}
                    {cancelados!.cantidad === 1 ? 'pedido cancelado' : 'pedidos cancelados'}.
                  </li>
                )}
                {(resumen.cantidadItemsCancelados ?? 0) > 0 && (
                  <li>
                    {resumen.cantidadItemsCancelados}{' '}
                    {resumen.cantidadItemsCancelados === 1
                      ? 'platillo quitado'
                      : 'platillos quitados'}{' '}
                    de una cuenta.
                  </li>
                )}
                {(resumen.descuentoPromoTotal ?? 0) > 0 && (
                  <li>
                    <strong>{money(resumen.descuentoPromoTotal ?? 0)}</strong> en descuentos que
                    diste
                    {(resumen.cantidadPromosAplicadas ?? 0) > 0
                      ? ` (${resumen.cantidadPromosAplicadas} promociones)`
                      : ''}
                    .
                  </li>
                )}
              </ul>
            </section>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700">
              Nada raro en este periodo: sin cancelaciones, sin descuentos y sin devoluciones
              pendientes.
            </div>
          )}

          {/* ── 4. Qué se vendió ─────────────────────────────────────────── */}
          <section className="card">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" aria-hidden="true" />
              <h2 className="font-bold">Qué se vendió</h2>
            </div>
            {productos.length === 0 && paquetes.length === 0 ? (
              <p className="mt-2 text-sm text-slate-600">
                Se cobró {money(resumen.totalVentas)}, pero ningún producto quedó registrado en
                este periodo. Avísanos si lo ves: no debería pasar.
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-slate-200">
                {[
                  ...productos.map((p) => ({
                    clave: `p-${p.productoId}`,
                    nombre: p.nombre,
                    tipo: 'Producto',
                    cantidad: p.cantidadVendida,
                    importe: p.ingresoTotal,
                  })),
                  ...paquetes.map((p) => ({
                    clave: `q-${p.paqueteId}`,
                    nombre: p.nombre,
                    tipo: 'Paquete',
                    cantidad: p.cantidadVendida,
                    importe: p.ingresoTotal,
                  })),
                ]
                  .sort((a, b) => b.cantidad - a.cantidad)
                  .slice(0, 10)
                  .map((f) => (
                    <li key={f.clave} className="flex items-center gap-3 py-2">
                      <span className="w-10 shrink-0 text-right font-mono font-bold">
                        {f.cantidad}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{f.nombre}</span>
                      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                        {f.tipo}
                      </span>
                      <span className="w-28 shrink-0 text-right font-mono font-semibold">
                        {money(f.importe)}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </section>

          {/* ── 5. Para tu contadora — plegado: es para otro momento del mes ── */}
          <section className="card">
            <button
              type="button"
              onClick={() => setVerContadora((v) => !v)}
              className="flex min-h-[44px] w-full items-center gap-2 text-left font-bold"
            >
              {verContadora ? (
                <ChevronDown className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              )}
              Para tu contadora
            </button>
            {verContadora && (
              <dl className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt>Venta de comida (sin envíos)</dt>
                  <dd className="font-mono font-semibold">
                    {money(resumen.ingresosAlimentos ?? 0)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Envíos que cobraste tú</dt>
                  <dd className="font-mono font-semibold">{money(resumen.totalEnviosCobrados)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Envíos que cobró el repartidor (no entraron a caja)</dt>
                  <dd className="font-mono font-semibold">
                    {money(resumen.totalEnviosContraEntrega ?? 0)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Propinas</dt>
                  <dd className="font-mono font-semibold">{money(resumen.totalPropinas)}</dd>
                </div>
                {/*
                  El sistema guarda los precios con el IVA dentro —así captura el menú el negocio—
                  así que el impuesto registrado es cero. Decir "IVA: $0.00" sería mentir con una
                  cifra; se dice lo que pasa, que es lo que la contadora necesita saber.
                */}
                <p className="pt-2 text-sm text-slate-700">
                  Tus precios ya llevan el IVA incluido, así que el sistema no lo separa: entrega lo
                  cobrado. Las facturas se hacen por fuera, de modo que aquí no se distingue qué
                  venta terminó facturada.
                </p>
              </dl>
            )}
            {/*
              Sprint 157 — El papel para la contadora. Sólo el dueño: el servidor tampoco se lo da a
              nadie más (sección `contabilidad`), esto sólo evita enseñar un botón que fallaría.
            */}
            {verContadora && esDueno && <ReporteContadoraPdf />}
          </section>

          {/* ── 6. Clientes — al final: es marketing, no cierre del día ──── */}
          <section className="card">
            <button
              type="button"
              onClick={() => setVerClientes((v) => !v)}
              className="flex min-h-[44px] w-full items-center gap-2 text-left font-bold"
            >
              {verClientes ? (
                <ChevronDown className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              )}
              <Users className="h-5 w-5 text-primary" aria-hidden="true" />
              Clientes que más te compran
            </button>
            {verClientes &&
              (clientes.length === 0 ? (
                <p className="mt-2 text-sm text-slate-600">
                  Sin pedidos a domicilio con teléfono registrado en este periodo.
                </p>
              ) : (
                <ul className="mt-2 divide-y divide-slate-200">
                  {clientes.map((c, i) => (
                    /*
                      Retícula de columnas fijas y fila acotada. Antes era `justify-between` a
                      ancho completo: a 1920 px el importe quedaba a 376 px del nombre y pegado al
                      borde, justo debajo del icono del teléfono — se leían encimados.
                    */
                    <li
                      key={c.telefono}
                      className="flex max-w-2xl items-center gap-6 py-2 text-sm"
                    >
                      <span className="w-6 shrink-0 text-right font-mono text-slate-500">
                        {i + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-semibold">
                        {c.nombre ?? 'Sin nombre'}
                      </span>
                      <span className="shrink-0 text-slate-600">
                        {c.cantidadPedidos}{' '}
                        {c.cantidadPedidos === 1 ? 'pedido' : 'pedidos'}
                      </span>
                      <span className="w-28 shrink-0 text-right font-mono font-semibold tabular-nums">
                        {money(c.totalGastado)}
                      </span>
                    </li>
                  ))}
                </ul>
              ))}
          </section>
        </>
      )}
    </div>
  );
}
